import React from 'react';
import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';
import { OG_IMAGE, SITE_ORIGIN, Locale, ogLocale, pathLocale, staticAlternates } from '../utils/seo';

export interface AlternateLink {
    hrefLang: string;
    href: string;
}

interface SeoProps {
    title: string;
    description: string;
    path: string;
    canonical?: string;
    alternates?: AlternateLink[];
    type?: 'website' | 'article';
    jsonLd?: unknown;
    noindex?: boolean;
    publishedTime?: string;
    lang?: Locale;
}

const Seo: React.FC<SeoProps> = ({
    title,
    description,
    path,
    canonical,
    alternates,
    type = 'website',
    jsonLd,
    noindex = false,
    publishedTime,
    lang,
}) => {
    const { pathname } = useLocation();
    const language = lang ?? pathLocale(pathname) ?? 'en';
    const canonicalHref = canonical ?? staticAlternates(path).find((link) => link.hrefLang === language)?.href ?? `${SITE_ORIGIN}/`;
    const links = alternates ?? staticAlternates(path);

    return (
        <Head htmlAttributes={{ lang: language }}>
            <title>{title}</title>
            <meta name="description" content={description} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <link rel="canonical" href={canonicalHref} />
            {links.map((link) => (
                <link rel="alternate" key={link.hrefLang} href={link.href} hrefLang={link.hrefLang} />
            ))}
            <meta property="og:site_name" content="Calensync" />
            <meta property="og:type" content={type} />
            <meta property="og:locale" content={ogLocale(language)} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonicalHref} />
            <meta property="og:image" content={OG_IMAGE} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={OG_IMAGE} />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {jsonLd != null && (
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
        </Head>
    );
};

export default Seo;

export const NoIndex: React.FC<{ title: string; description?: string }> = ({ title, description }) => {
    return (
        <Head>
            <title>{title}</title>
            {description && <meta name="description" content={description} />}
            <meta name="robots" content="noindex, nofollow" />
        </Head>
    );
};
